import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import { unwrapArrayPayload } from "../../utils/teacherApi";
import DataLoader from "../Spinner/DataLoader";
import CopyFooter from "../CopyFooter/CopyFooter";
import CreateFinalProjectModal from "../CreateFinalProjectModal/CreateFinalProjectModal";
import GradeSubmissionModal from "../GradeSubmissionModal/GradeSubmissionModal";

const styles = `
  .gpt-wrapper {
    display: flex; flex-direction: column;
    font-family: 'Inter','Roboto',sans-serif;
    font-size: 12px; height: 100%; overflow: hidden;
  }
  .gpt-toolbar {
    display: flex; width: 100%; background: #f3f4f6;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    align-items: center; justify-content: space-between;
    padding: 4px 8px; margin-bottom: 8px; border-radius: 6px;
  }
  .gpt-toolbar-left  { display: flex; align-items: center; gap: 4px; }
  .gpt-toolbar-right { display: flex; align-items: center; gap: 4px; }
  .gpt-filter-btn {
    display: inline-flex; align-items: center; justify-content: center;
    background: #fff; color: #374151; border: 1px solid #dee2e6;
    padding: 6px 16px; cursor: pointer; font-size: 12px; font-weight: 600;
    min-width: 70px; transition: background 0.15s, color 0.15s;
  }
  .gpt-filter-btn:first-child { border-radius: 6px 0 0 6px; }
  .gpt-filter-btn:last-child  { border-radius: 0 6px 6px 0; margin-left: -1px; }
  .gpt-filter-btn.active { background: #198754; color:#fff; border-color:#198754; }
  .gpt-filter-btn:disabled { cursor: not-allowed; opacity: 0.6; }
  .gpt-btn-success {
    display: inline-flex; align-items: center; gap: 4px;
    background: #2563eb; color: #fff; border: 1px solid #2563eb;
    border-radius: 6px; height: 32px; padding: 6px 12px; cursor: pointer; font-size: 12px; font-weight: 600; line-height: 1.2;
  }
  .gpt-btn-success:hover { background: #1d4ed8; border-color: #1d4ed8; }
  .gpt-btn-refresh {
    display: flex; align-items: center; justify-content: center;
    background: var(--color-primary); color: #fff; border: none;
    border-radius: 6px; width: 32px; height: 32px; cursor: pointer; font-size: 13px; flex-shrink: 0;
  }
  .gpt-btn-refresh:hover { background: var(--color-primary-dark); }
  .gpt-btn-icon {
    display: inline-flex; align-items: center; justify-content: center;
    border: none; border-radius: 5px; padding: 4px 8px;
    cursor: pointer; font-size: 12px; transition: opacity 0.15s;
  }
  .gpt-btn-icon:hover { opacity: 0.75; }
  .gpt-table-wrapper {
    background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    overflow: hidden; border-radius: 4px;
    flex: 1; display: flex; flex-direction: column; min-height: 0;
  }
  .gpt-table-scroll { overflow-x: auto; overflow-y: auto; flex: 1; min-height: 0; }
  .gpt-table-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
  .gpt-table-scroll::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 4px; }
  .gpt-table-scroll::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 4px; }
  .gpt-table-scroll::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
  .gpt-data-table { width: 100%; border-collapse: collapse; font-size: 12px; color: #374151; }
  .gpt-data-table thead tr { background: #f3f4f6; position: sticky; top: 0; z-index: 5; }
  .gpt-data-table th {
    padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e7eb;
    font-weight: 700; font-size: 11px; text-transform: uppercase; white-space: nowrap;
  }
  .gpt-data-table tbody tr { border-bottom: 1px solid #e5e7eb; transition: background 0.15s; }
  .gpt-data-table tbody tr:hover { background: #f9fafb; }
  .gpt-data-table td { padding: 6px 12px; white-space: nowrap; }
  .gpt-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
  .gpt-badge-active   { background: #dcfce7; color: #166534; }
  .gpt-badge-finished { background: #f3f4f6; color: #6b7280; }
  .gpt-days-warn { color: #d97706; font-weight: 700; }
  .gpt-days-over { color: #dc2626; font-weight: 700; }
  .gpt-days-ok   { color: #16a34a; font-weight: 700; }
  .gpt-spinner-wrap {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 40px 0; gap: 8px; color: #9ca3af;
  }
  .gpt-empty { text-align: center; padding: 40px 0; color: #9ca3af; font-size: 12px; }
`;

const STATUS_OPTIONS = [
  { id: "Yeni", label: "Yeni" },
  { id: "Bitdi", label: "Bitdi" },
];

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("az-AZ");
};

const GroupProjectsTab = () => {
  const [statusFilter, setStatusFilter] = useState("Yeni");
  const [assignments, setAssignments]   = useState([]);
  const [loading, setLoading]           = useState(false);
  const [showCreate, setShowCreate]     = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [copiedText, setCopiedText]     = useState("");

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const [res] = await Promise.all([
        apiFetch(`${BASE_URL}/teacher-group-projects/get-teacher-group-projects?status=${statusFilter}`),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      if (!res.ok) { setAssignments([]); return; }
      const data = await res.json();
      setAssignments(unwrapArrayPayload(data, ["list", "items", "content"]));
    } catch (e) {
      console.error(e);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const handleCreateClose = () => setShowCreate(false);

  return (
    <div className="gpt-wrapper">
      <style>{styles}</style>

      <div className="gpt-toolbar">
        <div className="gpt-toolbar-left">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.id} type="button" disabled={loading}
              onClick={() => setStatusFilter(opt.id)}
              className={`gpt-filter-btn${statusFilter === opt.id ? " active" : ""}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="gpt-toolbar-right">
          <button className="gpt-btn-refresh" onClick={fetchAssignments} title="Yenilə" disabled={loading}>
            <i className="bi bi-arrow-clockwise" />
          </button>
          <button className="gpt-btn-success" onClick={() => setShowCreate(true)}>
            <i className="bi bi-plus-lg" /> Yeni layihə
          </button>
        </div>
      </div>

      <div className="gpt-table-wrapper" style={{ position: "relative" }}>
        <DataLoader show={loading} />
        {!loading && assignments.length === 0 ? (
          <div className="gpt-empty">Bu status üzrə layihə tapılmadı.</div>
        ) : (
          <div className="gpt-table-scroll">
            <table className="gpt-data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Qrup</th>
                  <th>Layihə</th>
                  <th>Verilmə tarixi</th>
                  <th>Müddət</th>
                  <th>Status</th>
                  <th>Tələbə sayı</th>
                  <th>Gözləyənlər</th>
                  <th>Təsdiq olunanlar</th>
                  <th>Qəbul edilməyənlər</th>
                  <th>Əməliyyat</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((row, i) => {
                  return (
                    <tr key={row.id ?? i}>
                      <td>{row.id ?? "—"}</td>
                      <td onClick={() => setCopiedText(row.group ?? "—")}>{row.group ?? "—"}</td>
                      <td onClick={() => setCopiedText(row.project ?? "—")}><strong>{row.project ?? "—"}</strong></td>
                      <td onClick={() => setCopiedText(formatDate(row.beginDate))}>{formatDate(row.beginDate)}</td>
                      <td>{row.dayLimit ? `${row.dayLimit} gün` : "—"}</td>
                      <td>
                        <span className={`gpt-badge ${row.status === "Bitdi" ? "gpt-badge-finished" : "gpt-badge-active"}`}>
                          {row.status ?? "—"}
                        </span>
                      </td>
                      <td>{row.studentCount ?? 0}</td>
                      <td>{row.pendingCount ?? 0}</td>
                      <td>{row.approvedCount ?? 0}</td>
                      <td>{row.rejectedCount ?? 0}</td>
                      <td>
                        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                          <button
                            className="gpt-btn-icon"
                            style={{ background: "none", border: "none", color: "#6366f1", fontWeight: 600, fontSize: 12, padding: "2px 4px", display: "inline-flex", alignItems: "center", gap: 4 }}
                            title="Tələbə layihələrinə bax"
                            onClick={() => setSelectedProject(row)}
                          >
                            Aç <i className="bi bi-box-arrow-up-right" style={{ fontSize: 11 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <CopyFooter value={copiedText} />
      </div>

      <CreateFinalProjectModal
        show={showCreate}
        onHide={handleCreateClose}
        onSuccess={fetchAssignments}
      />

      <GradeSubmissionModal
        show={!!selectedProject}
        assignment={selectedProject}
        onHide={() => setSelectedProject(null)}
      />
    </div>
  );
};

export default GroupProjectsTab;
